import type { ItemDTO } from "@barkup/shared";

/**
 * Client-side mirror of the server's `q` predicate.
 *
 * The archive's search endpoint (apps/api/src/routes/liff/items.ts) matches a
 * query as a case-insensitive substring OR'd across exactly three columns —
 * `filename`, `ogTitle`, `linkUrl` — and orders results `createdAt desc, id
 * desc`. Everything here reproduces that, deliberately and narrowly, so the
 * results painted from already-loaded items are always a *subset* of what the
 * server will return, never a superset.
 *
 * That subset property is the whole point: the instant local pass can only ever
 * be missing rows the server is about to add, never showing rows the server will
 * take away. Nothing on screen has to be retracted when the real answer lands,
 * which is what lets the two phases merge without a flicker.
 *
 * If the server's predicate ever changes (extra columns, tokenised search,
 * ranking), this file must change with it — a divergence shows up as results
 * that appear on keystroke and then vanish.
 */

/** The searchable columns, in the same order the server ORs them. */
function haystack(item: ItemDTO): (string | null)[] {
  return [item.filename, item.ogTitle, item.linkUrl];
}

/**
 * The server rejects a `q` longer than this outright (`z.string().max(200)` in
 * apps/api/src/routes/liff/items.ts), and a 400 surfaces to the user as
 * "โหลดรายการไม่สำเร็จ" with a retry button that can only ever fail again.
 *
 * Not a theoretical limit: search is documented as matching `linkUrl`, and
 * pasting a long tracking URL straight into the field is the obvious way to
 * look one up. Clamping here — plus `maxlength` on the input — keeps the query
 * inside what the server will answer instead of turning a paste into a dead end.
 */
export const MAX_QUERY_LENGTH = 200;

/**
 * Normalises a raw input value into the string actually searched with.
 *
 * Trimming is the one intentional deviation from "send exactly what was typed":
 * mobile keyboards (LINE's webview included) love to append a space after an
 * autocompleted word, and `contains " report "` matches nothing. Trimming is
 * applied to BOTH the local pass and the outgoing request, so the two still
 * agree — that's what keeps this a mirror rather than a divergence. The length
 * clamp is applied in the same place, for the same reason.
 */
export function normalizeQuery(q: string): string {
  return q.trim().slice(0, MAX_QUERY_LENGTH);
}

/** Case-insensitive substring, matching Postgres `contains` + `mode: "insensitive"` on ASCII. */
function contains(haystackValue: string | null, needleLower: string): boolean {
  return haystackValue !== null && haystackValue.toLowerCase().includes(needleLower);
}

/**
 * Filters an already-ordered list without reordering it.
 *
 * No relevance ranking, on purpose. The server answers in date order; if the
 * local pass ranked by match quality, every search would visibly re-sort itself
 * a few hundred milliseconds later as the authoritative result replaced it.
 * Keeping both passes in the same order means the server result only ever
 * *inserts* rows the local pass couldn't know about.
 */
export function filterByQuery(items: ItemDTO[], query: string): ItemDTO[] {
  if (!query) return items;
  const needle = query.toLowerCase();
  return items.filter((item) => haystack(item).some((field) => contains(field, needle)));
}

/** Server sort order: `createdAt desc, id desc`. Applied after merging pages so the local index stays in list order. */
export function compareItems(a: ItemDTO, b: ItemDTO): number {
  if (a.createdAt !== b.createdAt) return a.createdAt < b.createdAt ? 1 : -1;
  return a.id < b.id ? 1 : -1;
}

export interface Segment {
  text: string;
  hit: boolean;
}

/**
 * Splits a display string into matched / unmatched runs for emphasis.
 *
 * Returns a single unmatched segment when the query doesn't appear in this
 * particular string — which is a real case, not a bug: an item can match on
 * `linkUrl` while the row displays its `ogTitle`. Showing no emphasis there is
 * the honest outcome; inventing one would point at text that didn't match.
 *
 * The offsets found in the lowercased copy are used to slice the ORIGINAL, so
 * the two must be the same length. For a handful of characters they aren't —
 * "İ" (U+0130) lowercases to two code units — and every offset past such a
 * character is then shifted, which doesn't merely misplace the emphasis, it
 * slices the filename apart and renders the pieces in the wrong order. A
 * filename is not something this row may corrupt, so a string whose case
 * folding changes length gives up emphasis rather than risk it.
 */
export function highlightSegments(text: string, query: string): Segment[] {
  if (!query) return [{ text, hit: false }];
  const needle = query.toLowerCase();
  const lower = text.toLowerCase();
  if (lower.length !== text.length) return [{ text, hit: false }];

  const segments: Segment[] = [];
  let cursor = 0;
  for (;;) {
    const at = lower.indexOf(needle, cursor);
    if (at === -1) break;
    if (at > cursor) segments.push({ text: text.slice(cursor, at), hit: false });
    segments.push({ text: text.slice(at, at + needle.length), hit: true });
    cursor = at + needle.length;
  }
  if (segments.length === 0) return [{ text, hit: false }];
  if (cursor < text.length) segments.push({ text: text.slice(cursor), hit: false });
  return segments;
}
